/**
 * The core animation set.
 *
 * ## The problem these clips actually solve
 *
 * She has rigid legs (D-015). There is no knee, no ankle, and no foot roll, so
 * every trick that normally sells a walk is unavailable. If you animate rigid
 * legs the obvious way — swing them back and forth — you get a wind-up toy.
 *
 * So the weight is carried by the **body**, and the legs mostly keep up:
 *
 * - **Vertical bob** at twice the stride frequency. A quadruped's body drops
 *   when a diagonal pair is loaded and lifts as it pushes off. This is the
 *   single biggest contributor to reading as heavy rather than as sliding.
 *   Getting its PHASE right relative to the legs matters more than its size.
 * - **Body pitch**, a small nose-down dip as the forelegs take the load.
 * - **Body roll**, tipping toward whichever diagonal pair is planted. Small —
 *   it reads as weight shifting, and it is the reason she does not look like
 *   she is on rails.
 * - **Squash**, on landings and hard changes only, capped at 5 % by the rig.
 *
 * Legs run in a **diagonal gait**: front-left with back-right, front-right with
 * back-left. That is what a real pig does at a walk and a trot, and it is also
 * what makes the roll and body cycles line up into something readable.
 *
 * ## Why every curve here is asymmetric
 *
 * A leg swinging on a sine wave spends equal time forward and back, which is
 * exactly what a foot must NOT do — a planted foot moves slowly and steadily
 * (it is stuck to the ground, and she moves over it), while a lifting foot
 * whips through. Spec §0 bans linear lerps and foot slide; `stancePhase()` is
 * where that is actually addressed.
 *
 * ## What is not here
 *
 * Nothing drives head, ear or snout from a mood value. Those channels are left
 * near-neutral by the locomotion clips on purpose, so the perception solver
 * (D-002) owns them. The small head motion the gaits DO apply is mechanical
 * bob — her head moving because her body is moving, not because she feels
 * something.
 */

import { ease, type Clip, type LegPose, type Pose } from './types';

/** Radians of leg swing at each gait. Small: her legs are 78 mm of visible limb. */
const SWING = { creep: 0.2, walk: 0.34, trot: 0.52 };

/**
 * Foot lift, metres. Tiny by necessity — her whole leg is under 8 cm — but it
 * is what stops the feet grinding through the floor on the return stroke.
 */
const LIFT = { creep: 0.008, walk: 0.016, trot: 0.028 };

/**
 * Maps a linear cycle phase to a leg's swing position, with a slow stance and a
 * fast swing.
 *
 * `duty` is the fraction of the cycle the foot spends planted. 0.6 at a walk
 * means it is on the ground for 60 % of the cycle, travelling backward at a
 * near-constant rate, then whips forward through the remaining 40 %.
 *
 * Returns -1..1, where -1 is fully back and +1 fully forward.
 */
function stancePhase(phase: number, duty: number): number {
  const t = phase % 1;
  if (t < duty) {
    // Planted. Linear on purpose: the foot is stuck to the ground and she is
    // moving over it, so a constant rate here is what "not sliding" looks like.
    return 1 - (t / duty) * 2;
  }
  // Airborne. Eased, so it accelerates off the ground and settles into the plant
  // rather than arriving at a hard stop.
  return -1 + ease.inOut((t - duty) / (1 - duty)) * 2;
}

/** How high the foot is at a given phase. Zero throughout stance, arc in swing. */
function liftPhase(phase: number, duty: number, height: number): number {
  const t = phase % 1;
  if (t < duty) return 0;
  return Math.sin(((t - duty) / (1 - duty)) * Math.PI) * height;
}

function setLeg(out: LegPose, swing: number, splay: number, lift: number): void {
  out.swing = swing;
  out.splay = splay;
  out.lift = lift;
}

/**
 * The shared locomotion body. Every ground gait is this function with different
 * numbers, which is deliberate: walk and trot must be the SAME motion at
 * different intensities, or blending between them produces a third gait that
 * belongs to neither.
 */
interface GaitParams {
  swing: number;
  lift: number;
  duty: number;
  /** Vertical bob amplitude, metres. */
  bob: number;
  /** Nose-down dip as the forelegs load, radians. */
  pitch: number;
  /** Weight-shift roll, radians. */
  roll: number;
  /** Body squash at the bottom of the bob. 1 = none. */
  squash: number;
  /** How much the head counter-bobs. Mechanical, never emotional. */
  headBob: number;
  /** Tail sway amplitude, radians. */
  tail: number;
  /** Crouch: a constant downward offset and a forward pitch. */
  crouch: number;
  /**
   * Radians the tail is held DOWN in this gait, on top of its usual sway.
   *
   * Reported at review: creeping under the tunnel, her tail "always appears to
   * be sticking in the object above the pig". A tail carried at standing height
   * while the rest of her ducks is not a small visual error — it is the one part
   * of her that clips through the thing she is deliberately fitting under, so it
   * undermines the whole verb.
   */
  tailDrop: number;
}

function sampleGait(phase: number, p: GaitParams, out: Pose): void {
  copyNeutral(out);

  // Diagonal pairs. FL+BR lead, FR+BL are half a cycle behind.
  const a = phase;
  const b = phase + 0.5;
  setLeg(out.legFL, stancePhase(a, p.duty) * p.swing, 0, liftPhase(a, p.duty, p.lift));
  setLeg(out.legBR, stancePhase(a, p.duty) * p.swing, 0, liftPhase(a, p.duty, p.lift));
  setLeg(out.legFR, stancePhase(b, p.duty) * p.swing, 0, liftPhase(b, p.duty, p.lift));
  setLeg(out.legBL, stancePhase(b, p.duty) * p.swing, 0, liftPhase(b, p.duty, p.lift));

  // Bob runs at TWICE stride frequency — the body drops once per diagonal pair,
  // so twice per full cycle. This is the phase relationship that makes it read
  // as weight; get it wrong and she looks like she is floating on a wave.
  const bobCycle = ease.cycleCos(phase * 2);
  out.bob = -p.bob * 0.5 * (1 - bobCycle) - p.crouch;

  // Squash at the bottom of the bob, stretch very slightly at the top.
  const compression = (1 - bobCycle) * 0.5;
  out.squashY = 1 - (p.squash - 1) * compression;
  out.squashX = 1 + (p.squash - 1) * compression * 0.6;
  out.squashZ = out.squashX;

  // Pitch dips as the forelegs load, which is a quarter cycle after the bob peak.
  out.pitch = p.pitch * ease.cycle(phase * 2 - 0.25) + p.crouch * 1.6;
  // Roll tips toward the planted diagonal. Once per cycle, not twice.
  out.roll = p.roll * ease.cycle(phase);

  // Head counter-bobs against the body — it lags, so it is a quarter cycle late.
  out.headPitch = p.headBob * ease.cycle(phase * 2 - 0.25);
  out.tailYaw = p.tail * ease.cycle(phase);
  // The tail lifts a little with the bob. Pigs' tails are never still.
  out.tailPitch = p.tail * 0.4 * ease.cycleCos(phase * 2) - p.tailDrop;
}

/**
 * Reset a pose in place.
 *
 * Deliberately NOT `neutralPose()` — that allocates, and every clip calls this
 * on every sample, several times per frame once blending is involved. At 60 Hz
 * on integrated graphics that is pure collector pressure for no benefit.
 */
function copyNeutral(out: Pose): void {
  out.bob = 0;
  out.squashX = 1;
  out.squashY = 1;
  out.squashZ = 1;
  out.pitch = 0;
  out.roll = 0;
  out.yaw = 0;
  out.tuck = 0;
  setLeg(out.legFL, 0, 0, 0);
  setLeg(out.legFR, 0, 0, 0);
  setLeg(out.legBL, 0, 0, 0);
  setLeg(out.legBR, 0, 0, 0);
  out.headPitch = 0;
  out.headYaw = 0;
  out.headRoll = 0;
  out.snoutPitch = 0;
  out.snoutYaw = 0;
  out.earLFlick = 0;
  out.earLSplay = 0;
  out.earRFlick = 0;
  out.earRSplay = 0;
  out.tailPitch = 0;
  out.tailYaw = 0;
}

const WALK: GaitParams = {
  swing: SWING.walk,
  lift: LIFT.walk,
  // 0.62 — a walk keeps each foot down for well over half the cycle, which is
  // what separates it from a trot at the same speed.
  duty: 0.62,
  bob: 0.011,
  pitch: 0.035,
  roll: 0.028,
  squash: 1.02,
  headBob: 0.05,
  tail: 0.09,
  crouch: 0,
  tailDrop: 0,
};

const TROT: GaitParams = {
  swing: SWING.trot,
  lift: LIFT.trot,
  // 0.48 — below half, so there are brief moments with no foot planted. That is
  // the definition of a trot and it is where the lift comes from.
  duty: 0.48,
  bob: 0.019,
  pitch: 0.05,
  roll: 0.02,
  squash: 1.035,
  headBob: 0.07,
  tail: 0.14,
  crouch: 0,
  tailDrop: 0,
};

const CREEP: GaitParams = {
  swing: SWING.creep,
  lift: LIFT.creep,
  // 0.74 — a creeping animal keeps three feet down nearly always. It is also
  // slow and deliberate, which is the whole point of the stealth gait.
  duty: 0.74,
  bob: 0.004,
  pitch: 0.012,
  roll: 0.012,
  squash: 1.008,
  headBob: 0.02,
  tail: 0.03,
  // She drops and lengthens. Spec §4 calls this "crouch-low" and the camera
  // comes down to snout height to meet it.
  // 0.060 m, and this number is NOT free — it must match what the COLLIDER does.
  //
  // `ROSALIE_TUNING` shrinks the capsule from 0.32 m standing to `crouchHeight`
  // 0.26 m, a 60 mm drop. This was 26 mm, so her capsule fitted through the
  // 0.28 m crawl space and her body did not: measured at 0.299 m through a
  // 0.28 m gap. She passed through the roof in plain view while the physics
  // insisted she was clear, which is exactly the kind of disagreement that makes
  // a game feel untrustworthy — reported at review as her tail "sticking in the
  // object above the pig", though the tail was only the top 5 mm of it.
  //
  // `anim.spec.ts` now measures her highest world vertex against the real tunnel
  // clearance, so if either number moves without the other, that test fails.
  crouch: 0.06,
  // Enough to clear a tunnel roof she is otherwise fitting under. Not tucked —
  // a pinned tail reads as fear, and D-002 keeps her body language about
  // attention rather than emotion.
  tailDrop: 0.38,
};

function gaitClip(id: string, params: GaitParams, duration: number): Clip {
  return {
    id,
    duration,
    loop: true,
    sample(phase, out) {
      sampleGait(phase, params, out);
    },
  };
}

export const walkClip = gaitClip('walk', WALK, 0.72);
export const trotClip = gaitClip('trot', TROT, 0.44);
export const creepClip = gaitClip('creep', CREEP, 1.1);

/**
 * Idle, with variation.
 *
 * Spec §0 requires idle variation, and the cheap way to fake it is a random
 * timer. That is wrong here for a mechanical reason: clips must be pure
 * functions of phase (see `types.ts`) or a screenshot test cannot reproduce a
 * pose. So variation comes from **two cycles at incommensurable rates** — a
 * breathing cycle and a much slower weight-shift — which never quite repeat
 * together and are still perfectly deterministic.
 *
 * The breathing rate is not decorative. D-009 makes breathing the stealth UI,
 * and the visible breath must eventually agree with the audible one.
 */
export const idleClip: Clip = {
  id: 'idle',
  duration: 4.3,
  loop: true,
  sample(phase, out) {
    copyNeutral(out);

    // Breathing: the chest rises and falls, and she lengthens very slightly.
    // ~14 breaths per idle cycle is a small animal at rest.
    const breath = ease.cycle(phase * 3);
    out.bob = breath * 0.0035;
    out.squashY = 1 + breath * 0.012;
    out.squashZ = 1 - breath * 0.006;
    out.pitch = breath * 0.008;

    // Weight shift: a slow, wandering lean at a rate that does not divide the
    // breath cycle, so the pair never lands in the same place twice.
    const shift = ease.cycle(phase * 0.37);
    const shift2 = ease.cycle(phase * 0.61);
    out.roll = shift * 0.014;
    out.yaw = shift2 * 0.01;
    // Her weight moving means her legs take it unevenly.
    out.legFL.swing = shift * 0.02;
    out.legFR.swing = -shift * 0.02;
    out.legBL.swing = shift2 * 0.015;
    out.legBR.swing = -shift2 * 0.015;

    // The tail is never still. This is the clearest sign of life at a distance,
    // and it costs one sine.
    out.tailYaw = ease.cycle(phase * 1.7) * 0.11;
    out.tailPitch = ease.cycle(phase * 2.3) * 0.05;

    // Head settles with the breath. Mechanical — her head moves because her
    // ribcage does. The perception solver owns everything intentional (D-002).
    out.headPitch = breath * 0.012;
  },
};

/**
 * Tuck and roll (spec §5.4).
 *
 * The verb the whole game's joy rests on, and the one clip where her rigid legs
 * are an advantage rather than a constraint: a tucked animal has no visible
 * legs, so there is nothing to fake.
 *
 * She pulls her legs in hard, tucks her head down, and the body rotation itself
 * is applied by the integrator — this clip is the TUCK, not the spin. Keeping
 * them separate means the spin rate can follow real speed while the tuck stays
 * a fixed shape.
 */
export const rollClip: Clip = {
  id: 'roll',
  duration: 0.5,
  loop: true,
  sample(phase, out) {
    copyNeutral(out);

    // Legs fold under her and stay there. A little cycling remains so she does
    // not look like a frozen prop being rolled downhill.
    const churn = ease.cycle(phase) * 0.06;
    setLeg(out.legFL, -0.95 + churn, 0.12, 0.03);
    setLeg(out.legFR, -0.95 - churn, 0.12, 0.03);
    setLeg(out.legBL, 0.95 - churn, 0.12, 0.03);
    setLeg(out.legBR, 0.95 + churn, 0.12, 0.03);

    // Tucked: she curls into a ball. This is the `tuck` channel, not squash —
    // see the note in types.ts and the D-002 amendment. At the 5 % squash cap
    // she stayed rigid and read as a log rolling end over end rather than as an
    // animal curling up, which is what "the body does not bend" was.
    //
    // Eased in over the first part of the clip so entering a roll is a visible
    // act of curling up rather than an instant shape change.
    out.tuck = 0.72 + 0.06 * ease.cycle(phase);
    out.squashY = 1.03;
    out.squashX = 1.03;
    out.bob = -0.012;
    out.headPitch = 0.38;
    // Ears pinned back. This is aerodynamic and physical, not emotional — an
    // ear left flapping at 4.5 m/s would read as a bug.
    out.earLFlick = -0.5;
    out.earRFlick = -0.5;
    out.earLSplay = -0.22;
    out.earRSplay = -0.22;
    out.tailPitch = -0.3;
  },
};

/**
 * Landing. A one-shot, played over whatever she is already doing.
 *
 * Structure is anticipation-free by necessity — she is already falling, so there
 * is nothing to anticipate — but the recovery carries all the weight:
 * **compress fast, recover slowly.** Equal timings read as a bounce, which is a
 * ball, not a stuffed animal. The legs splay outward under load, which is the
 * detail that makes it read as absorbing rather than as scaling.
 */
export const landingClip: Clip = {
  id: 'landing',
  duration: 0.42,
  loop: false,
  sample(phase, out) {
    copyNeutral(out);

    const t = Math.min(1, Math.max(0, phase));
    // Compression peaks at 18 % of the clip, then eases out over the rest.
    const impact = t < 0.18 ? ease.out(t / 0.18) : 1 - ease.inOut((t - 0.18) / 0.82);

    out.squashY = 1 - 0.05 * impact;
    out.squashX = 1 + 0.04 * impact;
    out.squashZ = 1 + 0.04 * impact;
    out.bob = -0.022 * impact;
    out.pitch = 0.06 * impact;

    // Legs take the load: they splay out and swing to brace.
    const splay = 0.16 * impact;
    setLeg(out.legFL, 0.12 * impact, splay, 0);
    setLeg(out.legFR, 0.12 * impact, splay, 0);
    setLeg(out.legBL, -0.12 * impact, splay, 0);
    setLeg(out.legBR, -0.12 * impact, splay, 0);

    // Head dips and comes back up a beat late — follow-through, and the reason
    // the landing reads as one event rather than as a body scaling briefly.
    const headLag = t < 0.3 ? ease.out(t / 0.3) : 1 - ease.inOut((t - 0.3) / 0.7);
    out.headPitch = 0.14 * headLag;
    out.earLFlick = -0.18 * headLag;
    out.earRFlick = -0.18 * headLag;
    out.tailPitch = -0.12 * headLag;
  },
};

/**
 * Scramble — hauling herself onto a ledge as tall as she is (D-012, D-017).
 *
 * This is the clip that makes the step ladder legible. Autostep takes the low
 * steps silently and the 0.48 m step refuses her forever; without a visible
 * effort in between, the ladder reads "easy, easy, blocked, blocked" and the
 * whole "built for boots, not for her" idea is invisible.
 *
 * So it is deliberately NOT smooth. Front legs reach and catch, the body hauls
 * up late and heavily, the back legs scrabble for purchase, and she lands with
 * a settle rather than arriving cleanly. `phase` is the controller's scramble
 * progress, so the clip is locked to the actual movement.
 */
export const scrambleClip: Clip = {
  id: 'scramble',
  duration: 0.72,
  loop: false,
  sample(phase, out) {
    copyNeutral(out);
    const t = Math.min(1, Math.max(0, phase));

    // Reach: forelegs go up and forward early, well ahead of the body.
    const reach = ease.out(Math.min(1, t / 0.35));
    // Haul: the body follows late, which is what makes it read as weight.
    const haul = ease.inOut(Math.min(1, Math.max(0, (t - 0.25) / 0.5)));
    // Settle: a small recovery at the top so she arrives rather than snapping.
    const settle = t > 0.78 ? ease.inOut((t - 0.78) / 0.22) : 0;

    setLeg(out.legFL, 0.95 * reach - 0.35 * settle, 0.1 * reach, 0.05 * reach);
    setLeg(out.legFR, 0.88 * reach - 0.35 * settle, 0.12 * reach, 0.045 * reach);
    // Back legs scrabble — out of phase with each other, which is what scrabbling
    // looks like and what a symmetric pair never does.
    const scrabble = ease.cycle(t * 3.5);
    setLeg(out.legBL, -0.5 * haul + 0.18 * scrabble, 0.08, 0.02 * Math.abs(scrabble));
    setLeg(out.legBR, -0.5 * haul - 0.18 * scrabble, 0.08, 0.02 * Math.abs(scrabble));

    // Nose up through the reach, levelling as she clears the edge.
    out.pitch = -0.34 * reach + 0.30 * haul + 0.10 * settle;
    out.bob = -0.012 * reach + 0.006 * settle;
    out.squashY = 1 - 0.03 * settle;
    out.squashX = 1 + 0.02 * settle;
    // Effort, not emotion: ears pin back under strain and release at the top.
    out.earLFlick = -0.3 * haul * (1 - settle);
    out.earRFlick = -0.3 * haul * (1 - settle);
    out.tailPitch = 0.25 * haul;
    // A little asymmetry so it never reads as a machine lifting evenly.
    out.roll = 0.05 * ease.cycle(t * 1.5) * haul;
  },
};

/** The set the player expects. Ordered as they appear in `player.ts`'s ClipSet. */
export const ROSALIE_CLIPS = {
  idle: idleClip,
  walk: walkClip,
  trot: trotClip,
  creep: creepClip,
  roll: rollClip,
  landing: landingClip,
  scramble: scrambleClip,
} as const;
